import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, ArrowLeft, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { listAssessmentsByEmail, trackEvent } from "../lib/api";

/**
 * Resume Assessment flow (client-side).
 * - User enters the email they used when starting the assessment.
 * - Backend returns ALL recent assessments (in_progress + submitted).
 * - Single draft → auto-open at the correct step.
 * - Multiple drafts → show a picker.
 * - Submitted records → show non-editable "under expert review" message.
 * - No magic-link auth yet — simple email-based lookup.
 */

const STATUS_META = {
  in_progress: { label: "Draft", tone: "blue" },
  submitted: { label: "Submitted", tone: "green" },
};

const SCENARIO_LABELS = {
  greenfield: "Greenfield / Pre-SAP",
  brownfield: "Brownfield / SAP upgrade",
  cloud_migration: "Cloud Migration",
};

// Map saved current_step → next route the user should land on to continue
// (mirrors the step transitions in Module1/2/3):
//   step 1 = Module 1 saved → continue at Module 2
//   step 2 = Module 2 saved → continue at Module 3
//   step 3 / 4 = Module 3 in progress or submitted → Module 3 (or confirmation)
function routeForStep(step) {
  if (!step || step < 1) return "/evaluate/module-1";
  if (step === 1) return "/evaluate/module-2";
  if (step === 2) return "/evaluate/module-3";
  return "/evaluate/module-3";
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ResumeAssessmentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => {
    const fromQuery = searchParams.get("email");
    const fromStorage = localStorage.getItem("warehousex_email");
    const raw = fromQuery || fromStorage || "";
    return typeof raw === "string" ? raw : "";
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null); // { email, count, assessments }

  /**
   * Normalize any candidate value into a trimmed, lowercased email string.
   * Guards against click-event objects, undefined, null, numbers, etc.
   */
  const toSafeEmail = (value) =>
    typeof value === "string" ? value.trim().toLowerCase() : "";

  // Auto-lookup when the page is opened via an email deep-link (e.g. reminder).
  useEffect(() => {
    const qp = searchParams.get("email");
    if (qp && typeof qp === "string" && qp.includes("@")) {
      // Kick off the lookup immediately so the user lands on their draft.
      runLookup(qp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runLookup = async (override) => {
    setError("");
    setResults(null);
    // Only accept a string override — click handlers may forward an event
    // object, which used to crash `.trim()` with "is not a function".
    const source = typeof override === "string" ? override : email;
    const safeEmail = toSafeEmail(source);
    if (!safeEmail) {
      setError("Please enter the email you used to start your evaluation.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeEmail)) {
      setError("Enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const data = await listAssessmentsByEmail(safeEmail);
      setResults(data);
      trackEvent("resume_lookup", safeEmail, { count: data?.count || 0 });

      const list = Array.isArray(data?.assessments) ? data.assessments : [];
      // Keep records the entered email can access: owner match by email OR an
      // active-collaborator record (tagged access_role by the backend — its
      // top-level email is the OWNER's, not the contributor's, so we must not
      // filter those out).
      const matches = list.filter((r) => {
        const recordEmail = toSafeEmail(r?.email);
        return (
          r?.access_role === "owner" ||
          r?.access_role === "collaborator" ||
          (recordEmail && recordEmail === safeEmail)
        );
      });
      const drafts = matches.filter((a) => a.status === "in_progress");
      // Auto-resume only when exactly one draft and no ambiguity
      if (drafts.length === 1 && matches.length === 1) {
        openAssessment(drafts[0], safeEmail);
      }
    } catch (e) {
      console.error(e);
      setError("Couldn't look up your assessment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openAssessment = (a, emailUsed) => {
    if (!a) return;
    try {
      const stash =
        toSafeEmail(emailUsed) ||
        toSafeEmail(a?.email) ||
        toSafeEmail(email);
      if (stash) localStorage.setItem("warehousex_email", stash);
      if (a.id) localStorage.setItem("warehousex_assessment_id", a.id);
    } catch { /* ignore storage errors */ }
    if (a.status === "submitted") {
      // No editing after submission — take them to the confirmation screen.
      trackEvent("resume_open_submitted", toSafeEmail(emailUsed) || toSafeEmail(a?.email));
      navigate("/evaluate/submitted");
      return;
    }
    trackEvent("resume_open_draft", toSafeEmail(emailUsed) || toSafeEmail(a?.email), {
      assessment_id: a.id, current_step: a.current_step,
    });
    navigate(routeForStep(a.current_step));
  };

  const handleKey = (e) => { if (e.key === "Enter") runLookup(); };

  const list = results?.assessments || [];
  const drafts = list.filter((a) => a.status === "in_progress");
  const submitted = list.filter((a) => a.status === "submitted");

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="resume-page">
      <Header />

      <main className="flex-1 pt-10 sm:pt-14 pb-12">
        <div className="w-full max-w-xl mx-auto px-4 sm:px-6">
          {/* Single back link on the top-left — no other header CTAs */}
          <button
            data-testid="resume-back-btn"
            onClick={() => navigate("/smartstrategy")}
            className="inline-flex items-center gap-1.5 text-sm text-[#4B5563] hover:text-[#025979] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to SmartStrategy
          </button>

          {/* Page header */}
          <div className="mb-6">
            <h1
              data-testid="resume-title"
              className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1F2937]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Continue where you left off
            </h1>
            <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">
              Enter the email you used when you started your evaluation. We&apos;ll pull up your saved draft so you can pick up where you stopped.
            </p>
          </div>

          {/* Email card */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-6 sm:p-7">
            <Label htmlFor="resume-email" className="text-xs font-medium text-[#374151] mb-1.5 block">
              Email address <span className="text-red-500">*</span>
            </Label>
            <div className="flex flex-col sm:flex-row gap-2.5">
              <Input
                id="resume-email"
                data-testid="resume-email-input"
                type="email"
                placeholder="e.g. sarah@acme.com"
                value={email}
                onChange={(e) => setEmail(e?.target?.value ?? "")}
                onKeyDown={handleKey}
                disabled={loading}
                className="rounded-sm border-[#D1D5DB] focus:border-[#025979] focus:ring-[#025979] flex-1"
              />
              <Button
                data-testid="resume-lookup-btn"
                onClick={() => runLookup()}
                disabled={loading}
                className="bg-[#025979] hover:bg-[#03738C] text-white text-sm font-medium rounded-sm transition-colors shadow-sm disabled:opacity-50"
              >
                {loading ? "Looking up…" : "Find my evaluation"}
                {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
            </div>
            {error && (
              <p data-testid="resume-error" className="text-xs text-red-500 mt-2">
                {error}
              </p>
            )}
            <p className="text-[11px] text-[#9CA3AF] mt-3 leading-relaxed">
              We only use your email to locate your saved draft — no password or magic link required.
            </p>
          </div>

          {/* Results */}
          {results && (
            <div className="mt-6" data-testid="resume-results">
              {list.length === 0 && (
                <EmptyState onStart={() => navigate("/evaluate/module-1")} />
              )}

              {drafts.length > 0 && (
                <div className="mb-5">
                  <p className="text-[11px] font-semibold text-[#025979] uppercase tracking-wider mb-2">
                    {drafts.length === 1 ? "1 draft in progress" : `${drafts.length} drafts in progress`}
                  </p>
                  <div className="space-y-2.5">
                    {drafts.map((a) => (
                      <DraftRow key={a.id} a={a} onResume={() => openAssessment(a, toSafeEmail(email))} />
                    ))}
                  </div>
                </div>
              )}

              {submitted.length > 0 && (
                <div className="mb-5">
                  <p className="text-[11px] font-semibold text-[#065F46] uppercase tracking-wider mb-2">
                    {submitted.length === 1 ? "1 submitted evaluation" : `${submitted.length} submitted evaluations`}
                  </p>
                  <div className="space-y-2.5">
                    {submitted.map((a) => (
                      <SubmittedRow
                        key={a.id}
                        a={a}
                        onView={() => openAssessment(a, toSafeEmail(email))}
                      />
                    ))}
                  </div>
                </div>
              )}

              {list.length > 0 && (
                <div className="text-center mt-4">
                  <button
                    data-testid="resume-start-new-link"
                    onClick={() => navigate("/evaluate/module-1")}
                    className="text-xs text-[#025979] hover:text-[#03738C] font-medium inline-flex items-center gap-1.5 transition-colors"
                  >
                    Or start a new evaluation
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function DraftRow({ a, onResume }) {
  const progress = a.progress_percent || 0;
  return (
    <div
      data-testid={`resume-draft-${a.id}`}
      className="bg-white border border-[#025979]/30 border-l-4 rounded-sm p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-[#111827] truncate">{a.company_name || "Unnamed company"}</p>
          <StatusPill status={a.status} />
        </div>
        <p className="text-[11px] text-[#6B7280] mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span>{SCENARIO_LABELS[a.scenario_type] || a.scenario_type || "—"}</span>
          <span className="text-[#9CA3AF]">•</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" strokeWidth={1.5} />
            Last updated {formatDate(a.updated_at)}
          </span>
        </p>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex-1 h-1 bg-[#E5E7EB] rounded-full overflow-hidden max-w-xs">
            <div
              className="h-full bg-[#025979] transition-all"
              style={{ width: `${Math.max(4, Math.min(100, progress))}%` }}
            />
          </div>
          <span className="text-[10px] font-medium text-[#025979]">{progress}%</span>
        </div>
      </div>
      <Button
        data-testid={`resume-btn-${a.id}`}
        onClick={onResume}
        className="bg-[#025979] hover:bg-[#03738C] text-white text-sm font-medium rounded-sm transition-colors shadow-sm shrink-0"
      >
        Resume
        <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
    </div>
  );
}

function SubmittedRow({ a, onView }) {
  return (
    <div
      data-testid={`resume-submitted-${a.id}`}
      className="bg-[#ECFDF5] border border-[#065F46]/20 rounded-sm p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-4 h-4 text-[#065F46] mt-0.5 shrink-0" strokeWidth={1.75} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-[#111827] truncate">{a.company_name || "Unnamed company"}</p>
            <StatusPill status={a.status} />
          </div>
          <p className="text-xs text-[#065F46] mt-1 leading-relaxed">
            This evaluation has already been submitted and is under expert review.
          </p>
          <p className="text-[11px] text-[#6B7280] mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span>{SCENARIO_LABELS[a.scenario_type] || a.scenario_type || "—"}</span>
            <span className="text-[#9CA3AF]">•</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" strokeWidth={1.5} />
              Submitted {formatDate(a.updated_at)}
            </span>
          </p>
        </div>
        <Button
          data-testid={`resume-view-submitted-${a.id}`}
          onClick={onView}
          variant="outline"
          className="rounded-sm text-xs border-[#065F46]/30 text-[#065F46] hover:bg-[#065F46]/5 hover:text-[#065F46] shrink-0"
        >
          View status
        </Button>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status, tone: "slate" };
  const cls = meta.tone === "green"
    ? "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15"
    : "bg-[#E0F2FE] text-[#025979] border-[#025979]/20";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 border rounded-sm text-[10px] font-medium ${cls}`}>
      {meta.label}
    </span>
  );
}

function EmptyState({ onStart }) {
  return (
    <div
      data-testid="resume-empty"
      className="bg-white border border-[#E5E7EB] rounded-sm p-6 text-center"
    >
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F1F5F9] mb-3">
        <AlertCircle className="w-5 h-5 text-[#6B7280]" strokeWidth={1.5} />
      </div>
      <p className="text-sm font-medium text-[#111827]">No saved evaluation found</p>
      <p className="text-xs text-[#6B7280] mt-1 leading-relaxed max-w-sm mx-auto">
        We couldn&apos;t find an evaluation for that email. Please check the
        email or start a new evaluation.
      </p>
      <Button
        data-testid="resume-empty-start-btn"
        onClick={onStart}
        className="mt-4 bg-[#025979] hover:bg-[#03738C] text-white text-sm font-medium rounded-sm transition-colors shadow-sm"
      >
        Start New Evaluation
        <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
    </div>
  );
}
